import { Permission, UserRole } from '@today/types'

/** The core admin sections, keyed for React lists and tests (TO-189 REQ-001). */
export enum AdminSectionKey {
	TENANT_CONFIG = 'tenant-config',
	ORGANIZATION = 'organization',
	BRAND = 'brand',
	CHANNELS = 'channels',
	FILE_EXCHANGE = 'file-exchange',
	IMPORTS = 'imports',
	SSO = 'sso',
	LOCALE_SETTINGS = 'locale-settings',
	NOTIFICATION_TEMPLATES = 'notification-templates',
	PRODUCTS = 'products',
	LEGAL = 'legal',
	LEGAL_APPROVALS = 'legal-approvals',
	CUSTOMERS = 'customers',
	COMPLIANCE = 'compliance',
	REALTIME = 'realtime',
}

/** What a viewer must hold to see a section: a permission atom, or (platform tools) a role. */
export type AdminSectionGrant = { permission: Permission } | { role: UserRole }

/**
 * One core admin section. `path` is absolute, except for a `tenantScoped`
 * section, where it is appended to `/t/{viewer's own slug}/admin`.
 */
export interface AdminSectionEntry {
	key: AdminSectionKey
	label: string
	grant: AdminSectionGrant
	path: string
	tenantScoped: boolean
}

const tenantSection = (key: AdminSectionKey, label: string, permission: Permission, path: string) => ({
	key,
	label,
	grant: { permission },
	path,
	tenantScoped: true,
})

const appSection = (key: AdminSectionKey, label: string, grant: AdminSectionGrant, path: string) => ({
	key,
	label,
	grant,
	path,
	tenantScoped: false,
})

/**
 * The core admin sections with a shipped destination, in menu order. A section
 * whose page ships later (Users, AI, Costs, …) is appended by its owning story.
 * Each is gated on the administrative permission its destination enforces, so
 * front-line users (read-only atoms) see none of them.
 */
export const ADMIN_SECTION_CATALOG: readonly AdminSectionEntry[] = [
	tenantSection(AdminSectionKey.TENANT_CONFIG, 'Tenant configuration', Permission.TENANT_CONFIG_MANAGE, '/config'),
	tenantSection(AdminSectionKey.ORGANIZATION, 'Organization', Permission.TENANT_CONFIG_MANAGE, '/config/organization'),
	tenantSection(AdminSectionKey.BRAND, 'Brand', Permission.TENANT_CONFIG_MANAGE, '/config/brand'),
	tenantSection(
		AdminSectionKey.CHANNELS,
		'Channels & Integrations',
		Permission.TENANT_CONFIG_MANAGE,
		'/config/webhooks',
	),
	tenantSection(
		AdminSectionKey.FILE_EXCHANGE,
		'File exchange',
		Permission.TENANT_CONFIG_MANAGE,
		'/config/file-exchange',
	),
	// TO-399: import monitoring — the executions list and drill-down.
	tenantSection(AdminSectionKey.IMPORTS, 'Imports', Permission.IMPORT_EXECUTION_READ, '/imports'),
	tenantSection(AdminSectionKey.SSO, 'SSO', Permission.TENANT_MANAGE_IDP, '/sso'),
	tenantSection(
		AdminSectionKey.LOCALE_SETTINGS,
		'Locale settings',
		Permission.TENANT_MANAGE_SETTINGS,
		'/locale-settings',
	),
	tenantSection(
		AdminSectionKey.NOTIFICATION_TEMPLATES,
		'Notification templates',
		Permission.NOTIFICATION_TEMPLATES_MANAGE,
		'/notification-templates',
	),
	appSection(AdminSectionKey.PRODUCTS, 'Products', { permission: Permission.PRODUCT_WRITE }, '/products'),
	appSection(AdminSectionKey.LEGAL, 'Legal', { permission: Permission.CONTENT_WRITE }, '/content/texts'),
	appSection(
		AdminSectionKey.LEGAL_APPROVALS,
		'Legal approvals',
		{ permission: Permission.CONTENT_APPROVE },
		'/content/inbox',
	),
	appSection(AdminSectionKey.CUSTOMERS, 'Customers', { permission: Permission.CUSTOMER_READ_ALL }, '/customers'),
	// TO-232: GDPR erasure + subject access; the page links to its Access requests sibling.
	appSection(
		AdminSectionKey.COMPLIANCE,
		'Compliance',
		{ permission: Permission.COMPLIANCE_READ },
		'/admin/compliance/deletions',
	),
	appSection(AdminSectionKey.REALTIME, 'Realtime diagnostics', { role: UserRole.SUPER_ADMIN }, '/admin/realtime'),
]
